/**
 * useObjectUrl - React hook for safe object URL lifecycle management
 * 
 * ENHANCEMENT 1: Automatically revokes object URLs on unmount or when fileId changes
 * Prevents memory leaks from orphaned blob URLs
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import fileStore from '../lib/fileStore';

/**
 * Hook to safely manage a single object URL
 * @param {string|null} fileId - File ID to create URL for
 * @returns {string|null} - Object URL or null
 */
export function useObjectUrl(fileId) {
  const [url, setUrl] = useState(null);
  const revokeRef = useRef(null);
  
  useEffect(() => {
    let mounted = true;
    
    // Revoke previous URL if exists
    if (revokeRef.current) {
      revokeRef.current();
      revokeRef.current = null;
    }
    
    if (!fileId) {
      setUrl(null);
      return;
    }
    
    // Load new URL
    fileStore.getObjectUrlForFileId(fileId).then(result => {
      if (mounted && result) {
        setUrl(result.url);
        revokeRef.current = result.revoke;
      }
    });
    
    return () => {
      mounted = false;
      if (revokeRef.current) {
        revokeRef.current();
        revokeRef.current = null;
      }
    };
  }, [fileId]);
  
  return url;
}

/**
 * Hook to safely manage multiple object URLs
 * @param {string[]} fileIds - Array of file IDs
 * @returns {string[]} - Array of object URLs
 */
export function useObjectUrls(fileIds) {
  const [urls, setUrls] = useState([]);
  const revokersRef = useRef([]);
  
  useEffect(() => {
    let mounted = true;
    
    // Revoke all previous URLs
    revokersRef.current.forEach(revoke => revoke());
    revokersRef.current = [];
    
    if (!fileIds || fileIds.length === 0) {
      setUrls([]);
      return;
    }
    
    // Load all URLs
    Promise.all(
      fileIds.map(fileId => fileStore.getObjectUrlForFileId(fileId))
    ).then(results => {
      if (mounted) {
        const validResults = results.filter(r => r !== null);
        setUrls(validResults.map(r => r.url));
        revokersRef.current = validResults.map(r => r.revoke);
      }
    });
    
    return () => {
      mounted = false;
      revokersRef.current.forEach(revoke => revoke());
      revokersRef.current = [];
    };
  }, [fileIds?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  
  return urls;
}

/**
 * Hook for managing a temporary object URL from a Blob
 * Useful for preview before saving
 * @param {Blob|null} blob - Blob to create URL for
 * @returns {string|null} - Object URL or null
 */
export function useBlobUrl(blob) {
  const [url, setUrl] = useState(null);
  
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [blob]);
  
  return url;
}

/**
 * Hook for batch image loading with cleanup
 * Designed for list views where images need to be loaded efficiently
 * @param {Array<{id: string, images: string[]}>} entities - Entities with image file IDs
 * @returns {Map<string, string>} - Map of entityId -> first image URL
 */
export function useEntityThumbnails(entities) {
  const [thumbnails, setThumbnails] = useState(new Map());
  const revokersRef = useRef(new Map());
  
  useEffect(() => {
    let mounted = true;
    
    // Revoke old thumbnails
    revokersRef.current.forEach(revoke => revoke());
    revokersRef.current.clear();
    
    if (!entities || entities.length === 0) {
      setThumbnails(new Map());
      return;
    }
    
    // Load first image for each entity
    const loadThumbnails = async () => {
      const newThumbnails = new Map();
      const newRevokers = new Map();
      
      for (const entity of entities) {
        if (entity.images && entity.images.length > 0) {
          const firstImageId = entity.images[0];
          // Skip if already base64
          if (firstImageId && firstImageId.startsWith('file_')) {
            const result = await fileStore.getObjectUrlForFileId(firstImageId);
            if (result && mounted) {
              newThumbnails.set(entity.id, result.url);
              newRevokers.set(entity.id, result.revoke);
            }
          } else if (firstImageId) {
            // Already a URL or base64
            newThumbnails.set(entity.id, firstImageId);
          }
        }
      }
      
      if (mounted) {
        setThumbnails(newThumbnails);
        revokersRef.current = newRevokers;
      }
    };
    
    loadThumbnails();
    
    return () => {
      mounted = false;
      revokersRef.current.forEach(revoke => revoke());
      revokersRef.current.clear();
    };
  }, [entities]);
  
  return thumbnails;
}

export default {
  useObjectUrl,
  useObjectUrls,
  useBlobUrl,
  useEntityThumbnails
};
